import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, TriangleAlert, Users, Wrench, Plus, ArrowRight, Fuel, Banknote, FileDown } from "lucide-react";
import { toast } from "sonner";
import { VBarChart, HBarChart } from "../components/Charts";
import api, { API } from "../lib/api";
import { AlertBadge } from "../components/AlertBadge";
import { daysUntil, alertLevel } from "../lib/alerts";

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="nb-card p-6 mc-pop">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="font-heading font-black text-5xl mt-2">{value}</p>
      </div>
      <div
        className="w-12 h-12 rounded-xl border-2 border-slate-900 flex items-center justify-center nb-shadow"
        style={{ background: color }}
      >
        <Icon strokeWidth={2.5} size={24} className="text-slate-900" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, alertes: 0, binomes_actifs: 0, indisponibles: 0 });
  const [vehicles, setVehicles] = useState([]);
  const [topConso, setTopConso] = useState({ month: "", top: [] });
  const [monthlyCost, setMonthlyCost] = useState({ months: [] });
  const [fleetComp, setFleetComp] = useState({ vehicles: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [s, v, t, m, c] = await Promise.all([
      api.get("/stats"),
      api.get("/vehicles"),
      api.get("/stats/top-consumers"),
      api.get("/stats/monthly-cost"),
      api.get("/stats/fleet-comparison"),
    ]);
    setStats(s.data);
    setVehicles(v.data);
    setTopConso(t.data);
    setMonthlyCost(m.data);
    setFleetComp(c.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const exportComparisonPdf = async () => {
    try {
      const res = await fetch(`${API}/stats/fleet-comparison/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "comparatif_flotte.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Comparatif exporté en PDF");
    } catch {
      toast.error("Échec de l'export PDF");
    }
  };

  const urgentList = vehicles
    .flatMap((v) =>
      ["controle_6mois_date", "controle_annuel_date"].map((f) => ({
        v,
        field: f,
        days: daysUntil(v[f]),
        label: f === "controle_6mois_date" ? "CT 6 mois" : "CT annuel",
      }))
    )
    .filter((x) => x.days !== null && x.days < 40)
    .sort((a, b) => a.days - b.days)
    .slice(0, 6);

  const costData = (monthlyCost.months || []).map((m) => {
    const [y, mo] = m.month.split("-");
    return { label: `${mo}/${y.slice(2)}`, montant: m.montant, litres: m.litres };
  });
  const compData = (fleetComp.vehicles || []).map((v) => ({
    label: v.immatriculation,
    conso: v.avg_conso,
    ref: v.conso_reference,
    over: v.conso_reference != null && v.avg_conso > v.conso_reference,
    fill: v.conso_reference != null && v.avg_conso > v.conso_reference ? "#EF4444" : "#4F46E5",
  }));

  return (
    <div className="ml-64 p-8 min-h-screen">
      <div className="flex items-center justify-between mb-8 mc-slide-up">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Tableau de bord</h1>
          <p className="text-slate-500 font-medium mt-1">Vue d'ensemble de votre flotte MobicarsVSD</p>
        </div>
        <button
          data-testid="dashboard-add-vehicle"
          onClick={() => navigate("/vehicules?nouveau=1")}
          className="flex items-center gap-2 bg-[#4F46E5] text-white font-bold px-5 py-3 rounded-xl nb-btn"
        >
          <Plus strokeWidth={2.5} size={20} /> Nouveau véhicule
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={Car} label="Véhicules" value={stats.total} color="#A5B4FC" testid="stat-total" />
        <StatCard icon={TriangleAlert} label="Alertes contrôle" value={stats.alertes} color="#FB923C" testid="stat-alertes" />
        <StatCard icon={Users} label="Binômes actifs" value={stats.binomes_actifs} color="#4ADE80" testid="stat-binomes" />
        <StatCard icon={Wrench} label="Indisponibles" value={stats.indisponibles} color="#FBBF24" testid="stat-indispo" />
      </div>

      <div className="nb-card p-6 mc-pop">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-black text-2xl">Contrôles à surveiller</h2>
          <button
            data-testid="see-all-vehicles"
            onClick={() => navigate("/vehicules")}
            className="flex items-center gap-1 text-sm font-bold text-[#4F46E5] hover:underline"
          >
            Tout voir <ArrowRight size={16} strokeWidth={2.5} />
          </button>
        </div>

        {loading ? (
          <p className="text-slate-400 font-medium py-8 text-center">Chargement...</p>
        ) : urgentList.length === 0 ? (
          <div className="py-10 text-center" data-testid="no-alerts">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#4ADE80] border-2 border-slate-900 nb-shadow font-bold">
              Aucun contrôle urgent 🎉
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {urgentList.map((x, i) => (
              <div
                key={`${x.v.id}-${x.field}`}
                data-testid={`urgent-row-${i}`}
                onClick={() => navigate(`/vehicules/${x.v.id}`)}
                className="flex items-center justify-between gap-4 p-4 rounded-xl border-2 border-slate-900 bg-white hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="font-mono-plate font-bold bg-white border-2 border-slate-900 rounded px-3 py-1 text-sm uppercase relative pl-6">
                    <span className="absolute left-0 top-0 bottom-0 w-3 bg-[#4F46E5] rounded-l" />
                    {x.v.immatriculation}
                  </span>
                  <span className="font-semibold text-sm">{x.v.conducteur || "Sans conducteur"}</span>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{x.label}</span>
                </div>
                <AlertBadge dateStr={x.v[x.field]} label={x.label} testid={`urgent-badge-${i}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="nb-card p-6 mt-8 mc-pop" data-testid="top-consumers-card">
        <div className="flex items-center gap-2 mb-5">
          <Fuel size={22} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h2 className="font-heading font-black text-2xl">Plus gros consommateurs ce mois-ci</h2>
        </div>
        {topConso.top.length === 0 ? (
          <p className="text-slate-400 font-medium py-6 text-center" data-testid="top-consumers-empty">
            Aucune consommation enregistrée ce mois-ci.
          </p>
        ) : (
          <div className="space-y-3">
            {topConso.top.map((c, i) => {
              const max = topConso.top[0].litres || 1;
              const pct = Math.max(6, Math.round((c.litres / max) * 100));
              return (
                <div
                  key={c.vehicle_id}
                  data-testid={`top-consumer-${i}`}
                  onClick={() => navigate(`/vehicules/${c.vehicle_id}`)}
                  className="cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono-plate font-bold bg-white border-2 border-slate-900 rounded px-2.5 py-0.5 text-sm uppercase relative pl-5">
                        <span className="absolute left-0 top-0 bottom-0 w-2.5 bg-[#4F46E5] rounded-l" />
                        {c.immatriculation}
                      </span>
                      {c.pleins ? <span className="text-xs font-bold text-slate-400">{c.pleins} plein(s)</span> : null}
                    </div>
                    <span className="font-heading font-black text-lg">
                      {c.litres} L{c.montant ? <span className="text-slate-400 text-sm font-bold"> · {c.montant} €</span> : null}
                    </span>
                  </div>
                  <div className="h-4 rounded-lg border-2 border-slate-900 bg-white overflow-hidden">
                    <div
                      className="h-full bg-[#FB923C] transition-all group-hover:bg-[#4F46E5]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="nb-card p-6 mt-8 mc-pop" data-testid="monthly-cost-card">
        <div className="flex items-center gap-2 mb-5">
          <Banknote size={22} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h2 className="font-heading font-black text-2xl">Budget carburant mensuel</h2>
        </div>
        {costData.length === 0 ? (
          <p className="text-slate-400 font-medium py-6 text-center" data-testid="monthly-cost-empty">
            Aucune dépense carburant enregistrée.
          </p>
        ) : (
          <VBarChart data={costData.map((d) => ({ label: d.label, value: d.montant }))} height={260} unit=" €" />
        )}
      </div>

      <div className="nb-card p-6 mt-8 mc-pop" data-testid="fleet-comparison-card">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Fuel size={22} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h2 className="font-heading font-black text-2xl">Comparatif consommation de la flotte</h2>
          <span className="text-xs font-bold flex items-center gap-1.5">
            <span className="w-3 h-3 rounded border-2 border-slate-900 bg-[#EF4444]" /> Dépasse la référence
          </span>
          {compData.length > 0 && (
            <button
              data-testid="export-comparison-pdf"
              onClick={exportComparisonPdf}
              className="ml-auto flex items-center gap-2 bg-white font-bold text-sm px-4 py-2 rounded-xl nb-btn"
            >
              <FileDown strokeWidth={2.5} size={18} /> Export PDF
            </button>
          )}
        </div>
        {compData.length === 0 ? (
          <p className="text-slate-400 font-medium py-6 text-center" data-testid="fleet-comparison-empty">
            Aucune consommation moyenne calculable (importez des pleins avec kilométrage).
          </p>
        ) : (
          <HBarChart
            data={compData.map((d) => ({ label: d.label, value: d.conso, ref: d.ref, over: d.over, fill: d.fill }))}
            unit=" L"
          />
        )}
      </div>
    </div>
  );
}
